import { useCallback, useMemo } from 'react';
import { Navigate } from 'react-router-dom';
import cn from 'bem-cn';

import { useGetChallengeQuery, useGetCoursesByIdsQuery } from '../../store';
import { WebAppLayout } from '../../layouts/WebAppLayout';
import { HeaderElements } from '../../components/Header';
import { NavigationElement, NavigationElements } from '../../components/Navigation';
import { Stack } from '../../components/UI';
import { useNavigateBack, useSlug, useWithAuth } from '../../hooks';
import { ChallengeInfo, ChallengeInfoSkeleton } from '../../components/ChallengeInfo';
import { isError404 } from '../../utils/error/isError';
import { RoutePaths } from '../../constants';
import { BackButton } from '../../components/BackButton';
import { getDaysUntil } from '../../utils/date/date';

import './ChallengePage.scss';

const b = cn('challenge-page');

export const ChallengePage = () => {
    const { slug: challengeId, isValidSlug: isValidChallengeId } = useSlug();
    const { withAuth } = useWithAuth();

    const { data: challenge, isFetching, isSuccess, error } = useGetChallengeQuery({ challengeId });
    const { data: relatedCourses, isFetching: isRelatedCoursesFetching } = useGetCoursesByIdsQuery(
        { ids: challenge?.relatedCourseIds || [] },
        { skip: !challenge?.relatedCourseIds?.length },
    );

    const isChallengeActive = getDaysUntil(challenge?.deadline) >= 0;

    const handleBackButtonClick = useNavigateBack();

    const headerElements: HeaderElements = useMemo(
        () => ({
            desktop: {
                left: { type: 'logo' },
                right: { type: 'profile' },
            },
            mobile: {
                left: { type: 'backButton', onClick: handleBackButtonClick },
                right: { type: 'profile' },
            },
        }),
        [handleBackButtonClick],
    );

    const openForm = useCallback(() => {
        if (isChallengeActive && challenge?.formUrl) {
            window.open(challenge.formUrl, '_blank', 'noopener, noreferrer');
        }
    }, [challenge?.formUrl, isChallengeActive]);

    const handleFormButtonClick = useCallback(() => {
        withAuth(openForm, {
            retpath: window.location.href,
        });
    }, [openForm, withAuth]);

    const formLinkButton: NavigationElement = useMemo(() => {
        return {
            text: 'Принимаю челлендж!',
            accent: true,
            disabled: !isChallengeActive,
            onClick: handleFormButtonClick,
        };
    }, [handleFormButtonClick, isChallengeActive]);

    const navigationElements: NavigationElements = useMemo(() => {
        return {
            desktop: [formLinkButton],
            mobile: [formLinkButton],
        };
    }, [formLinkButton]);

    if (!isValidChallengeId || isError404(error)) {
        return <Navigate to={RoutePaths.NotFound} replace />;
    }

    return (
        <WebAppLayout
            headerElements={headerElements}
            navigationElements={navigationElements}
            hideNavigation={!isChallengeActive}
        >
            <Stack className={b()}>
                {isFetching && <ChallengeInfoSkeleton />}
                {isSuccess && (
                    <ChallengeInfo
                        challenge={challenge}
                        courses={relatedCourses ?? []}
                        isCoursesLoading={isRelatedCoursesFetching}
                        isChallengeActive={isChallengeActive}
                    />
                )}
                <BackButton className={b('back-button')} />
            </Stack>
        </WebAppLayout>
    );
};
